import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft, Shield, Save, CheckCircle, Settings, Percent,
  DollarSign, Crown, Users, Clock, RefreshCw, Ticket,
  Trash2, AlertTriangle, FileText, Eye, XCircle, Check
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { supabase } from "../../lib/supabase";
import InviteCodesTab from "../../components/ui/InviteCodesTab";
import ExpertTierBadge from "../../components/ui/ExpertTierBadge";

const ADMIN_EMAIL = "337diesel@mail.ru";

export default function AdminPage() {
  const { user } = useAuth();
  const { platformSettings, updatePlatformSettings, refreshSettings } = useSubscription();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  const [experts, setExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [deletingExpertId, setDeletingExpertId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [expandedAppId, setExpandedAppId] = useState(null);

  const [editingSettings, setEditingSettings] = useState({
    commissionPercent: platformSettings.commissionPercent,
    expertTrialDays: platformSettings.expertTrialDays,
    expertMinRate: platformSettings.expertMinRate,
    expertMaxRate: platformSettings.expertMaxRate,
    bookingCancellationHours: platformSettings.bookingCancellationHours
  });

  useEffect(() => {
    setEditingSettings({
      commissionPercent: platformSettings.commissionPercent,
      expertTrialDays: platformSettings.expertTrialDays,
      expertMinRate: platformSettings.expertMinRate,
      expertMaxRate: platformSettings.expertMaxRate,
      bookingCancellationHours: platformSettings.bookingCancellationHours
    });
  }, [platformSettings]);

  const isAdmin = user?.email === ADMIN_EMAIL || user?.role === "admin";

  useEffect(() => {
    if (isAdmin && activeTab === "experts") {
      fetchExperts();
    }
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (isAdmin && activeTab === "applications") {
      fetchApplications();
    }
  }, [isAdmin, activeTab]);

  const fetchApplications = async () => {
    setLoadingApps(true);
    const { data } = await supabase
      .from("expert_applications")
      .select("*")
      .order("created_at", { ascending: false });
    setApplications(data || []);
    setLoadingApps(false);
  };

  const updateApplicationStatus = async (appId, status) => {
    const { error } = await supabase
      .from("expert_applications")
      .update({ status, reviewed_by: user?.id, reviewed_at: new Date().toISOString() })
      .eq("id", appId);
    if (!error) {
      setApplications((prev) => prev.map((a) => a.id === appId ? { ...a, status, reviewed_at: new Date().toISOString() } : a));
    }
  };

  const fetchExperts = async () => {
    setLoadingExperts(true);
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, name, email, role, created_at, expert_tier, invite_code_used")
      .eq("role", "expert")
      .order("created_at", { ascending: false });

    if (profilesData && profilesData.length > 0) {
      const expertIds = profilesData.map((p) => p.id);
      const { data: subsData } = await supabase
        .from("expert_subscriptions")
        .select("*")
        .in("expert_id", expertIds);

      const merged = profilesData.map((profile) => {
        const sub = subsData?.find((s) => s.expert_id === profile.id);
        const now = new Date();
        const trialEnd = sub ? new Date(sub.trial_end) : null;
        const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))) : 0;
        const isTrialActive = sub ? sub.is_active && trialEnd > now : false;

        return {
          ...profile,
          subscription: sub ? {
            plan: sub.plan,
            trialStart: sub.trial_start,
            trialEnd: sub.trial_end,
            isActive: sub.is_active,
            isTrialActive,
            daysLeft
          } : null
        };
      });
      setExperts(merged);
    } else {
      setExperts([]);
    }
    setLoadingExperts(false);
  };

  const handleDeleteExpert = async (expertId) => {
    setDeletingExpertId(expertId);
    try {
      await supabase.from("expert_subscriptions").delete().eq("expert_id", expertId);
      await supabase.from("reviews").delete().eq("expert_id", expertId);
      await supabase.from("bookings").delete().eq("expert_id", expertId);
      await supabase.from("consultation_notes").delete().eq("expert_id", expertId);
      await supabase.from("consultation_goals").delete().eq("expert_id", expertId);
      await supabase.from("articles").delete().eq("author_id", expertId);
      await supabase.from("expert_schedules").delete().eq("expert_id", expertId);
      await supabase.from("profiles").delete().eq("id", expertId);
      setExperts((prev) => prev.filter((e) => e.id !== expertId));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Failed to delete expert:", err);
    } finally {
      setDeletingExpertId(null);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Shield className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
            <h1 className="text-2xl font-medium text-white mb-4">
              Доступ запрещён
            </h1>
            <p className="text-lg font-extralight text-neutral-400 mb-6">
              Эта страница доступна только администратору
            </p>
            <Link
              to="/"
              className="text-primary-400 hover:text-primary-300 text-lg font-extralight"
            >
              Вернуться на главную
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSaveSettings = async () => {
    setSaving(true);
    const success = await updatePlatformSettings(editingSettings);
    setSaving(false);
    if (success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const tabs = [
    { id: "settings", label: "Настройки", icon: Settings },
    { id: "experts", label: "Эксперты", icon: Users },
    { id: "invites", label: "Инвайт-коды", icon: Ticket },
    { id: "applications", label: "Заявки", icon: FileText }
  ];

  return (
    <Layout hideFooter>
      <div id="admin_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-lg font-extralight">На главную</span>
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-primary-400" />
            <div>
              <h1 className="text-3xl font-medium text-white">
                Панель администратора
              </h1>
              <p className="text-lg font-extralight text-neutral-400">
                Управление комиссией, подписками, экспертами и инвайтами
              </p>
            </div>
          </div>

          <div className="flex gap-3 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-md text-lg font-extralight transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary-600/20 text-primary-400 border border-primary-600/40"
                    : "bg-neutral-900/50 text-neutral-400 border border-neutral-800 hover:bg-neutral-800"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "settings" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                <div className="flex items-center gap-3 mb-6">
                  <Percent className="w-5 h-5 text-primary-400" />
                  <h3 className="text-xl font-medium text-white">Комиссия платформы</h3>
                </div>
                <p className="text-lg font-extralight text-neutral-400 mb-6">
                  Процент, который удерживается из стоимости консультации эксперта. Клиент платит полную ставку эксперта, комиссия вычитается из неё.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-lg font-extralight text-neutral-400 mb-2">Размер комиссии (%)</label>
                    <input
                      type="number"
                      value={editingSettings.commissionPercent}
                      onChange={(e) => setEditingSettings((prev) => ({ ...prev, commissionPercent: Number(e.target.value) }))}
                      className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div className="p-4 rounded-md bg-neutral-800/50">
                    <p className="text-lg font-extralight text-neutral-500 mb-2">Пример (ставка 15 000 ₽):</p>
                    <p className="text-lg font-extralight text-neutral-300">
                      Клиент платит: <span className="text-white font-medium">15 000 ₽</span>
                    </p>
                    <p className="text-lg font-extralight text-neutral-300">
                      Платформа: <span className="text-primary-400 font-medium">{Math.round(15000 * editingSettings.commissionPercent / 100).toLocaleString("ru-RU")} ₽</span>
                    </p>
                    <p className="text-lg font-extralight text-neutral-300">
                      Эксперт: <span className="text-accent-400 font-medium">{(15000 - Math.round(15000 * editingSettings.commissionPercent / 100)).toLocaleString("ru-RU")} ₽</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                <div className="flex items-center gap-3 mb-6">
                  <Crown className="w-5 h-5 text-primary-400" />
                  <h3 className="text-xl font-medium text-white">Премиум-подписка для экспертов</h3>
                </div>
                <p className="text-lg font-extralight text-neutral-400 mb-6">
                  После регистрации эксперт автоматически получает бесплатную премиум-подписку на указанный срок.
                </p>
                <div>
                  <label className="block text-lg font-extralight text-neutral-400 mb-2">Срок бесплатного премиума (дней)</label>
                  <input
                    type="number"
                    value={editingSettings.expertTrialDays}
                    onChange={(e) => setEditingSettings((prev) => ({ ...prev, expertTrialDays: Number(e.target.value) }))}
                    className="w-full sm:w-64 px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                  />
                  <p className="text-lg font-extralight text-neutral-500 mt-2">
                    {editingSettings.expertTrialDays} дней = примерно {Math.round(editingSettings.expertTrialDays / 30)} мес.
                  </p>
                </div>
              </div>

              <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-5 h-5 text-primary-400" />
                  <h3 className="text-xl font-medium text-white">Лимиты и параметры</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-lg font-extralight text-neutral-400 mb-2">Мин. ставка (₽/час)</label>
                    <input
                      type="number"
                      value={editingSettings.expertMinRate}
                      onChange={(e) => setEditingSettings((prev) => ({ ...prev, expertMinRate: Number(e.target.value) }))}
                      className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-extralight text-neutral-400 mb-2">Макс. ставка (₽/час)</label>
                    <input
                      type="number"
                      value={editingSettings.expertMaxRate}
                      onChange={(e) => setEditingSettings((prev) => ({ ...prev, expertMaxRate: Number(e.target.value) }))}
                      className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-extralight text-neutral-400 mb-2">Время отмены (часов)</label>
                    <input
                      type="number"
                      value={editingSettings.bookingCancellationHours}
                      onChange={(e) => setEditingSettings((prev) => ({ ...prev, bookingCancellationHours: Number(e.target.value) }))}
                      className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  {saved && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2 text-accent-400"
                    >
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-lg font-extralight">Настройки сохранены</span>
                    </motion.div>
                  )}
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white text-lg font-medium transition-colors"
                >
                  <Save className="w-5 h-5" />
                  {saving ? "Сохранение..." : "Сохранить настройки"}
                </button>
              </div>
            </motion.div>
          )}

          {activeTab === "experts" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-medium text-white">Зарегистрированные эксперты</h2>
                <button
                  onClick={fetchExperts}
                  disabled={loadingExperts}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-lg font-extralight transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingExperts ? "animate-spin" : ""}`} />
                  Обновить
                </button>
              </div>

              {loadingExperts ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-neutral-500 animate-spin mx-auto mb-4" />
                  <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
                </div>
              ) : experts.length === 0 ? (
                <div className="text-center py-12 p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                  <Users className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <p className="text-lg font-extralight text-neutral-400">Пока нет зарегистрированных экспертов</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {experts.map((expert) => (
                    <div
                      key={expert.id}
                      className={`p-5 rounded-md border ${
                        expert.expert_tier === "premium" || expert.expert_tier === "top" || expert.expert_tier === "invited"
                          ? "bg-amber-950/10 border-amber-500/20"
                          : "bg-neutral-900/50 border-neutral-800"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-lg font-medium">
                            {expert.name?.[0] || "E"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="text-lg font-medium text-white">
                              {expert.name || "Без имени"}
                            </h4>
                            {expert.expert_tier && expert.expert_tier !== "regular" && (
                              <ExpertTierBadge tier={expert.expert_tier} size="md" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-lg font-extralight text-neutral-400">{expert.email}</p>
                            {expert.invite_code_used && (
                              <span className="text-lg font-extralight text-neutral-600">
                                Код: {expert.invite_code_used}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                          {expert.subscription ? (
                            <>
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-lg font-extralight ${
                                expert.subscription.isTrialActive
                                  ? "bg-accent-500/10 text-accent-400"
                                  : "bg-red-500/10 text-red-400"
                              }`}>
                                <Crown className="w-4 h-4" />
                                {expert.subscription.isTrialActive
                                  ? `Премиум (${expert.subscription.daysLeft} дн.)`
                                  : "Подписка истекла"
                                }
                              </div>
                              <p className="text-lg font-extralight text-neutral-500 mt-1">
                                <Clock className="w-4 h-4 inline mr-1" />
                                до {new Date(expert.subscription.trialEnd).toLocaleDateString("ru-RU")}
                              </p>
                            </>
                          ) : (
                            <span className="text-lg font-extralight text-neutral-500">
                              Нет подписки
                            </span>
                          )}
                        </div>

                          {confirmDeleteId === expert.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDeleteExpert(expert.id)}
                                disabled={deletingExpertId === expert.id}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-lg font-extralight transition-colors"
                              >
                                {deletingExpertId === expert.id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                                Да
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-3 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-lg font-extralight transition-colors"
                              >
                                Нет
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(expert.id)}
                              className="p-2 rounded-md hover:bg-red-500/10 text-neutral-600 hover:text-red-400 transition-colors"
                              title="Удалить эксперта"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "invites" && (
            <InviteCodesTab userId={user?.id} />
          )}

          {activeTab === "applications" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-medium text-white">Заявки на статус эксперта</h2>
                <button
                  onClick={fetchApplications}
                  disabled={loadingApps}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-lg font-extralight transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingApps ? "animate-spin" : ""}`} />
                  Обновить
                </button>
              </div>

              {loadingApps ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-neutral-500 animate-spin mx-auto mb-4" />
                  <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12 p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
                  <FileText className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <p className="text-lg font-extralight text-neutral-400">Пока нет заявок от потенциальных экспертов</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map((app) => {
                    const ai = app.ai_summary || {};
                    const isExpanded = expandedAppId === app.id;
                    const statusColors = {
                      pending: "bg-amber-500/10 text-amber-400",
                      approved: "bg-green-500/10 text-green-400",
                      rejected: "bg-red-500/10 text-red-400",
                      archived: "bg-neutral-700/50 text-neutral-400"
                    };
                    const statusLabels = {
                      pending: "На рассмотрении",
                      approved: "Одобрена",
                      rejected: "Отклонена",
                      archived: "В архиве"
                    };
                    return (
                      <div key={app.id} className="rounded-md border border-neutral-800 bg-neutral-900/50 overflow-hidden">
                        <div
                          className="p-5 cursor-pointer hover:bg-neutral-800/30 transition-colors"
                          onClick={() => setExpandedAppId(isExpanded ? null : app.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-lg font-medium">{(ai.name || app.name)?.[0] || "?"}</span>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-lg font-medium text-white">{ai.name || app.name}</h4>
                              <p className="text-lg font-extralight text-neutral-400">
                                {app.email} {ai.title ? `· ${ai.title}` : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-md text-lg font-extralight ${statusColors[app.status]}`}>
                                {statusLabels[app.status]}
                              </span>
                              <span className="text-lg font-extralight text-neutral-600">
                                {new Date(app.created_at).toLocaleDateString("ru-RU")}
                              </span>
                              <Eye className="w-4 h-4 text-neutral-500" />
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            className="border-t border-neutral-800"
                          >
                            <div className="p-5 space-y-4">
                              {ai.bio && (
                                <div>
                                  <p className="text-lg font-extralight text-neutral-500 mb-1">О себе (AI саммари)</p>
                                  <p className="text-lg font-extralight text-neutral-300 leading-relaxed whitespace-pre-line">{ai.bio}</p>
                                </div>
                              )}
                              {ai.companies?.length > 0 && (
                                <div>
                                  <p className="text-lg font-extralight text-neutral-500 mb-2">Опыт работы</p>
                                  <div className="space-y-1.5">
                                    {ai.companies.map((c, i) => (
                                      <p key={i} className="text-lg font-extralight text-neutral-300">
                                        <span className="text-white font-medium">{c.name}</span> — {c.position} ({c.years})
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {ai.achievements?.length > 0 && (
                                <div>
                                  <p className="text-lg font-extralight text-neutral-500 mb-2">Достижения</p>
                                  <ul className="space-y-1">
                                    {ai.achievements.map((a, i) => (
                                      <li key={i} className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-accent-400 flex-shrink-0 mt-1" />
                                        <span className="text-lg font-extralight text-neutral-300">{a}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {ai.specializations?.length > 0 && (
                                <div>
                                  <p className="text-lg font-extralight text-neutral-500 mb-2">Специализации</p>
                                  <div className="flex flex-wrap gap-2">
                                    {ai.specializations.map((s, i) => (
                                      <span key={i} className="px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight">{s}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {app.cv_file_name && (
                                <p className="text-lg font-extralight text-neutral-600">
                                  Файл: {app.cv_file_name}
                                </p>
                              )}

                              {app.status === "pending" && (
                                <div className="flex gap-3 pt-2">
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, "approved")}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-green-600 hover:bg-green-500 text-white text-lg font-medium transition-colors"
                                  >
                                    <Check className="w-4 h-4" /> Одобрить
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, "rejected")}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-red-600/20 hover:bg-red-600/40 text-red-400 text-lg font-extralight transition-colors"
                                  >
                                    <XCircle className="w-4 h-4" /> Отклонить
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(app.id, "archived")}
                                    className="px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
                                  >
                                    В архив
                                  </button>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}